import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, Headphones, Lock, Mail, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import AuthPageLayout from '../components/auth/AuthPageLayout';
import NewtonsCradleLoader from '../components/common/NewtonsCradleLoader';
import { translations } from '../utils/translations';
import apiService from '../utils/apiService';

const trustFeatures = [
  { icon: Shield, title: 'Secure & Trusted', desc: 'Bank-grade security for your account and earnings.' },
  { icon: Lock, title: 'Private & Confidential', desc: 'Your information is safe and never shared.' },
  { icon: Headphones, title: '24/5 Support', desc: "We're here to help you succeed anytime." }
];

const inputClass =
  'w-full rounded-xl border border-gray-200 bg-white py-3 pl-11 pr-4 text-sm text-gray-900 placeholder-gray-400 outline-none transition focus:border-red-500 focus:ring-2 focus:ring-red-500/15';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('English');
  const t = translations[selectedLanguage] || translations.English;

  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage && translations[savedLanguage]) {
      setSelectedLanguage(savedLanguage);
    }
  }, []);

  useEffect(() => {
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail;
      if (newLanguage && translations[newLanguage]) {
        setSelectedLanguage(newLanguage);
      }
    };
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setError(t.pleaseEnterEmail);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await apiService.forgotPassword(email.trim());
      if (response.success) {
        setSuccess(true);
        setEmail('');
      } else {
        setError(response.message || t.emailNotFound);
      }
    } catch (err) {
      setError(err.message || t.emailNotFound);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageLayout>
      <div className="flex w-full max-w-[440px] flex-col items-center">
        <div className="w-full rounded-2xl border border-gray-100 bg-white px-7 py-8 shadow-[0_4px_24px_rgba(0,0,0,0.06)] sm:px-9 sm:py-9">
          <div className="mb-8 text-center">
            <h2 className="text-[1.75rem] font-bold leading-tight text-gray-900">{t.resetPassword}</h2>
            <p className="mt-2 text-sm text-gray-500">{t.enterEmailForReset}</p>
          </div>

          {!success ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-center text-xs text-red-600">
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="email" className="mb-2 block text-sm font-medium text-gray-800">
                  {t.email}
                </label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-400" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputClass}
                    placeholder={t.enterEmail}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#dc2626] py-3.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? (
                  <>
                    <NewtonsCradleLoader size="28px" color="#ffffff" />
                    <span>{t.sending}</span>
                  </>
                ) : (
                  <>
                    {t.sendResetLink}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          ) : (
            <div className="space-y-5 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-50">
                <svg className="h-7 w-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">{t.checkYourEmail}</h3>
                <p className="mt-2 text-sm text-gray-500">{t.resetInstructionsSent}</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSuccess(false);
                  setError('');
                }}
                className="w-full rounded-xl border border-gray-200 bg-white py-3 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
              >
                {t.tryAnotherEmail}
              </button>
            </div>
          )}

          <div className="mt-7 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 transition hover:text-gray-800"
            >
              <ArrowLeft className="h-4 w-4" />
              {t.backToSignIn}
            </Link>
          </div>
        </div>

        <div className="mt-10 grid w-full grid-cols-3 gap-4">
          {trustFeatures.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="text-center">
              <div className="mx-auto mb-2.5 flex h-10 w-10 items-center justify-center rounded-full border border-red-100 bg-red-50">
                <Icon className="h-[18px] w-[18px] text-red-600" />
              </div>
              <p className="text-xs font-semibold text-gray-800">{title}</p>
              <p className="mt-1 text-[10px] leading-snug text-gray-400">{desc}</p>
            </div>
          ))}
        </div>

        <p className="mt-8 text-center text-[11px] text-gray-400">
          © 2024 Broker Partner. All rights reserved.{' '}
          <span className="cursor-pointer hover:text-gray-500">Terms of Use</span>
          {' · '}
          <span className="cursor-pointer hover:text-gray-500">Privacy Policy</span>
        </p>
      </div>
    </AuthPageLayout>
  );
};

export default ForgotPassword;
